import { Component, OnInit, OnDestroy } from "@angular/core";
import {
  DataService,
  Dialog,
  GetActiveAdministratorQuery,
  SharedModule,
  GetAdministratorListDocument,
  GetAdministratorListQuery,
  Administrator,
  GetAdministratorListQueryVariables,
  GetCurrentUserQuery,
  I18nService,
} from "@bb-vendure/admin-ui/core";
import { marker as _ } from "@biesbjerg/ngx-translate-extract-marker";
import { combineLatest, map, take, takeUntil, Observable, Subject } from "rxjs";
import { FormBuilder, Validators } from "@angular/forms";
import { ID } from "@vendure/common/lib/shared-types";
import {
  CreateTicketInput,
  TicketPriority,
  TicketStatus,
  TicketType,
} from "src/generated-gql/graphql";
import {RichTextEditorComponent } from '../rich-text-editor/rich-text-editor.component'

@Component({
  selector: "create-ticket-dialog",
  standalone: true,
  imports: [SharedModule,RichTextEditorComponent],
  templateUrl: "./create.dialog.html",
  styleUrl: "./create.dialog.scss",
})
export default class CreateTicketRequest implements OnInit, OnDestroy, Dialog {
  private destroy$ = new Subject<void>();

  constructor(
    protected dataService: DataService,
    private fb: FormBuilder,
    private i18NService: I18nService
  ) {}

  resolveWith: (result?: CreateTicketInput) => void;
  isFromFloating = false;
  isInternal = false;
  currentAdmin$: Observable<GetActiveAdministratorQuery["activeAdministrator"]>;

  detailForm = this.fb.group({
    subject: ["", Validators.required],
    description: ["", Validators.required],
    url: [""],
    requester: ["", Validators.required],
    assignee: [""],
    priority: [TicketPriority.Medium],
    dueDate: ["", Validators.required],
  });

  selectedRequesterId: ID | undefined;
  adminList$: Observable<GetAdministratorListQuery["administrators"]["items"]>;

  me: GetCurrentUserQuery["me"];
  ticketPriorities = Object.values(TicketPriority);

  addCurrentRouteAsLink() {
    this.detailForm.patchValue({
      url: window.location.href,
    });
  }

  ngOnInit(): void {
    this.dataService.auth
      .currentUser()
      .single$.pipe(takeUntil(this.destroy$))
      .subscribe((u) => {
        this.me = u.me;
      });

    this.currentAdmin$ = this.dataService.administrator
      .getActiveAdministrator()
      .single$.pipe(
        map((data) => data.activeAdministrator),
        takeUntil(this.destroy$)
      );

    this.adminList$ = this.dataService
      .query(GetAdministratorListDocument)
      .single$.pipe(
        take(1),
        map((d) => d.administrators.items)
      );

    this.currentAdmin$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.selectedRequesterId = data?.id;
      this.detailForm.controls["requester"].disable();
      this.detailForm.patchValue({
        requester: data?.id,
      });
      if (this.isFromFloating) {
        this.addCurrentRouteAsLink();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  submit() {
    const { subject, description, dueDate, assignee, priority, url } =
      this.detailForm.value;
    this.resolveWith({
      assigneeId: assignee !== "" ? (assignee as any).id : null,
      title: subject ?? "",
      description: description ?? "",
      dueDate: dueDate,
      priority: priority,
      url,
      status: TicketStatus.New,
      type: this.isInternal ? TicketType.InternalTicket : TicketType.QaBullet,
    });
  }

  renderMe(user: GetAdministratorListQuery["administrators"]["items"][0]) {
    if (user.id === "") return null;
    if (this.me) {
      if (user.id === this.me.id) {
        return `${user.firstName} ${
          user.lastName
        } (${this.i18NService.translate("ticket.me")})`;
      }
    }
    return `${user.firstName} ${user.lastName}`;
  }

  trackByFn(item: any) {
    return item.id;
  }
}
